import { useNavigate } from 'react-router-dom';
import { onboardingApi, type OnboardingStep } from '@/api/client';
import { useAsync } from '@/hooks';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/Button';
import { adminNavItems } from '@/pages/navItems';

/** Fallback when API is unreachable — each step must link somewhere useful. */
const STEP_HREF: Record<string, string> = {
  company_profile: '/app/admin/settings',
  admin_user: '/app/admin/people',
  zones: '/app/admin/zones',
  vehicle_types: '/app/admin/settings?tab=vehicle-types',
  rate_card: '/app/admin/rate-cards',
  pay_card: '/app/admin/pay-cards',
  fleet_vehicle: '/app/admin/vehicles',
  crew_member: '/app/admin/people',
  client_account: '/app/admin/clients',
  integrations: '/app/admin/integrations',
  fuel_price: '/app/admin/fuel',
  hub_location: '/app/admin/addresses',
  dispatch_ready: '/app/admin/settings',
  addresses: '/app/admin/addresses',
  partners: '/app/admin/partners',
};

/** One-line explanation per step, plus what must be done first. */
const STEP_HELP: Record<string, { description: string; needs?: string[] }> = {
  company_profile: { description: 'Legal name, TIN and billing address that appear on invoices.' },
  admin_user: { description: 'At least one person who can sign in and manage the system.' },
  zones: { description: 'The areas you deliver to. Prices are set from zone to zone.' },
  vehicle_types: { description: 'The truck sizes you run, e.g. 6-wheeler and 10-wheeler.' },
  rate_card: {
    description: 'What you charge clients for each zone-to-zone trip, by vehicle type.',
    needs: ['zones', 'vehicle_types'],
  },
  pay_card: { description: 'What drivers and helpers earn per trip.', needs: ['zones', 'vehicle_types'] },
  fleet_vehicle: { description: 'Add at least one truck so trips can be dispatched.', needs: ['vehicle_types'] },
  crew_member: { description: 'Add a driver or helper who will use the crew app.' },
  client_account: {
    description: 'A client must be activated before trips can be booked for them.',
    needs: ['rate_card'],
  },
  addresses: { description: 'Saved pickup and delivery points so bookings are quicker.' },
  partners: { description: 'Subcontracted trucking companies, if you use any.' },
  integrations: { description: 'Connect Xero, maps, email and file storage.' },
  fuel_price: { description: 'Current diesel price, used for fuel cost tracking.' },
  hub_location: { description: 'Add your depot in the Address book as an internal hub.' },
  dispatch_ready: {
    description: 'Check how far ahead clients must book, then save or confirm the Booking rules.',
  },
};

const DEFAULT_CHECKLIST: OnboardingStep[] = [
  { id: 'company_profile', label: 'Company profile', completed: false, href: STEP_HREF.company_profile },
  { id: 'admin_user', label: 'Admin user account', completed: false, href: STEP_HREF.admin_user },
  { id: 'zones', label: 'Service zones', completed: false, href: STEP_HREF.zones },
  { id: 'vehicle_types', label: 'Vehicle types', completed: false, href: STEP_HREF.vehicle_types },
  { id: 'rate_card', label: 'Active rate card', completed: false, href: STEP_HREF.rate_card },
  { id: 'pay_card', label: 'Active pay card', completed: false, href: STEP_HREF.pay_card },
  { id: 'fleet_vehicle', label: 'Fleet vehicle', completed: false, href: STEP_HREF.fleet_vehicle },
  { id: 'crew_member', label: 'Crew member', completed: false, href: STEP_HREF.crew_member },
  { id: 'client_account', label: 'Active client', completed: false, href: STEP_HREF.client_account },
  { id: 'integrations', label: 'API integrations (Xero, Maps, Resend, Storage)', completed: false, essential: false, href: STEP_HREF.integrations },
  { id: 'fuel_price', label: 'Fuel price register', completed: false, essential: false, href: STEP_HREF.fuel_price },
  { id: 'hub_location', label: 'Hub / depot location', completed: false, href: STEP_HREF.hub_location },
  { id: 'dispatch_ready', label: 'Dispatch settings reviewed', completed: false, essential: false, href: STEP_HREF.dispatch_ready },
];

function resolveHref(step: OnboardingStep): string {
  if (step.href && step.href !== '/app/admin') return step.href;
  return STEP_HREF[step.id] ?? '/app/admin/settings';
}

export function AdminHome() {
  const navigate = useNavigate();
  const { data: checklist, loading, error, offline, reload } = useAsync(
    () => onboardingApi.checklist(),
    [],
  );

  const steps = (checklist ?? (error ? [] : DEFAULT_CHECKLIST)).map((s) => ({
    ...s,
    href: resolveHref(s),
  }));
  const completed = steps.filter((s) => s.completed).length;
  const progress = Math.round((completed / Math.max(steps.length, 1)) * 100);
  const essentials = steps.filter((s) => s.essential !== false);
  const optional = steps.filter((s) => s.essential === false);
  const nextEssential = essentials.find((s) => !s.completed);
  const labelById = new Map(steps.map((s) => [s.id, s.label]));
  const doneById = new Map(steps.map((s) => [s.id, s.completed]));

  const renderStep = (step: OnboardingStep, i: number) => {
    const href = resolveHref(step);
    const help = STEP_HELP[step.id];
    const missing = (help?.needs ?? []).filter((id) => doneById.has(id) && !doneById.get(id));
    return (
      <li key={step.id} className={`checklist-item ${step.completed ? 'done' : ''}`}>
        <span className="checklist-num">{i + 1}</span>
        <span className="checklist-label">
          <span className="checklist-title">{step.label}</span>
          {help && <span className="checklist-desc">{help.description}</span>}
          {!step.completed && missing.length > 0 && (
            <span className="checklist-needs">
              Do first: {missing.map((id) => labelById.get(id) ?? id).join(', ')}
            </span>
          )}
        </span>
        {step.completed ? (
          <span className="checklist-done">Done</span>
        ) : (
          <Button type="button" size="sm" variant="secondary" onClick={() => navigate(href)}>
            Start
          </Button>
        )}
      </li>
    );
  };

  return (
    <Layout navItems={adminNavItems}>
      <div className="page">
        <div className="page-header row-between">
          <div>
            <h1>Setup checklist</h1>
            <p className="text-muted">Everything DispatchDash needs before your team can book and dispatch trips</p>
          </div>
          <div className="row">
            <Button type="button" variant="secondary" size="sm" onClick={() => reload()}>Refresh checklist</Button>
            {!error && !loading && (nextEssential ? (
              <Button type="button" size="sm" onClick={() => navigate(resolveHref(nextEssential))}>
                Continue setup
              </Button>
            ) : (
              <Button type="button" size="sm" variant="secondary" onClick={() => navigate('/app/admin/settings')}>
                Review settings
              </Button>
            ))}
          </div>
        </div>

        <section className="checklist-section">
          <div className="checklist-header row-between">
            <div>
              <h2>Progress</h2>
              <p className="text-muted">
                {error
                  ? offline
                    ? 'Could not load checklist — you appear offline.'
                    : error
                  : `${essentials.filter((s) => s.completed).length} of ${essentials.length} essential steps complete · ${completed} of ${steps.length} overall`}
              </p>
            </div>
            {!error && (
              <div className="progress-ring">
                <span className="progress-value">{progress}%</span>
              </div>
            )}
          </div>

          {loading ? (
            <div className="loading-center"><div className="spinner" /></div>
          ) : error ? (
            <p className="text-urgent" style={{ padding: 'var(--space-md) 0' }}>
              {error}{' '}
              <Button type="button" size="sm" variant="secondary" onClick={() => reload()}>Retry</Button>
            </p>
          ) : (
            <>
              <h3 className="checklist-group">Essentials</h3>
              <p className="text-muted checklist-group-note">Needed before you can book real trips.</p>
              <ol className="checklist">{essentials.map((step, i) => renderStep(step, i))}</ol>
              {optional.length > 0 && (
                <>
                  <h3 className="checklist-group">Optional</h3>
                  <p className="text-muted checklist-group-note">Recommended — do these when you're ready.</p>
                  <ol className="checklist">
                    {optional.map((step, i) => renderStep(step, essentials.length + i))}
                  </ol>
                </>
              )}
            </>
          )}
        </section>

        <div className="admin-quick grid-3" style={{ marginTop: 'var(--space-xl)' }}>
          <button className="quick-card" type="button" onClick={() => navigate('/app/admin/integrations')}>
            <strong>Integrations</strong>
            <span>Xero, Google Maps, Email (Resend), file storage</span>
          </button>
          <button className="quick-card" type="button" onClick={() => navigate('/app/admin/settings')}>
            <strong>Company profile</strong>
            <span>Legal name, TIN, booking rules</span>
          </button>
          <button className="quick-card" type="button" onClick={() => navigate('/app/admin/rate-cards')}>
            <strong>Rate cards</strong>
            <span>Selling prices by zone</span>
          </button>
        </div>
      </div>

      <style>{`
        .checklist-section {
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          padding: var(--space-xl);
        }
        .checklist-header h2 { margin-bottom: var(--space-xs); }
        .progress-ring {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          border: 4px solid var(--color-sand);
          border-top-color: var(--color-forest);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .progress-value {
          font-weight: 700;
          color: var(--color-forest);
        }
        .checklist {
          list-style: none;
          padding: 0;
          margin: var(--space-lg) 0 0;
        }
        .checklist-item {
          display: flex;
          align-items: center;
          gap: var(--space-md);
          padding: var(--space-md);
          border-bottom: 1px solid var(--color-border);
        }
        .checklist-item:last-child { border-bottom: none; }
        .checklist-item.done { opacity: 0.6; }
        .checklist-num {
          width: 28px;
          height: 28px;
          background: var(--color-sand);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 0.8125rem;
          flex-shrink: 0;
        }
        .checklist-item.done .checklist-num {
          background: var(--color-forest);
          color: var(--color-sand);
        }
        .checklist-label { flex: 1; display: flex; flex-direction: column; gap: 2px; }
        .checklist-title { font-weight: 600; }
        .checklist-desc { font-size: 0.8125rem; color: var(--color-text-muted); }
        .checklist-needs { font-size: 0.75rem; color: var(--color-warning); font-weight: 600; }
        .checklist-group { margin: var(--space-lg) 0 0; font-size: 1rem; }
        .checklist-group-note { margin: 0; font-size: 0.8125rem; }
        .checklist-group + .checklist-group-note + .checklist { margin-top: var(--space-sm); }
        .checklist-done {
          font-size: 0.75rem;
          color: var(--color-status-complete);
          font-weight: 600;
        }
        .quick-card {
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          padding: var(--space-lg);
          cursor: pointer;
          text-align: left;
          transition: border-color 0.15s;
        }
        .quick-card:hover { border-color: var(--color-forest); }
        .quick-card strong { display: block; margin-bottom: var(--space-xs); color: var(--color-forest); }
        .quick-card span { font-size: 0.8125rem; color: var(--color-text-muted); }
      `}</style>
    </Layout>
  );
}
